import { Button } from '@accure/ui';
import { ChevronRight } from 'lucide-react';

import { Card } from '@/components/ui/card';
import type { Alert } from '@/entities/alert';
import { AlertType } from '@/entities/alert-type';
import { MetricChart } from '@/warranty-v3/pages/warranty/components/metrics/metric-chart';
import { GetMetricsQuery } from '@/warranty-v3/queries/get-metrics';

import { lookerStyle } from '../warranty-preview-shared/looker-style';
import { PreviewLayout } from '../warranty-preview-shared/preview-layout';
import { TableSuggestedActions } from '../warranty-preview-shared/table-suggested-actions';
import { WarrantyDetailsLink } from '../warranty-preview-shared/warranty-details-link';

// Only allow specific warranty alert types
type AllowedWarrantyTypes =
  | AlertType.WARRANTY_TAG
  | AlertType.WARRANTY_TAG_PCS
  | AlertType.WARRANTY_RTE
  | AlertType.WARRANTY_SOH;

export type WarrantyMetricPreviewProps = {
  alert: Alert & { type: AllowedWarrantyTypes };
};

export function WarrantyMetricPreview({ alert }: WarrantyMetricPreviewProps) {
  const style = lookerStyle();

  const metrics = GetMetricsQuery.useQuery({
    systemId: alert.id,
    alertType: alert.type,
  });

  if (metrics.isLoading) {
    return null;
  }

  if (metrics.isError || !metrics.data) {
    throw metrics.error;
  }

  return (
    <PreviewLayout
      chart={
        <div>
          <MetricChart timeBasedMetrics={metrics.data} />
        </div>
      }
      table={
        <Card className="flex h-full flex-col items-center rounded-lg border border-gray-200 bg-white px-2 py-3 shadow-lg">
          <h2 className={style.headline()}>Suggested Actions</h2>
          <TableSuggestedActions alert={alert} />
        </Card>
      }
      link={
        <WarrantyDetailsLink alert={alert}>
          <Button size="lg" data-testid="alert-get-more-details-button">
            Get more details
            <ChevronRight />
          </Button>
        </WarrantyDetailsLink>
      }
    />
  );
}
